import { Box, Typography, Chip, Divider } from "@mui/material";
import { EllipsisVertical, ClipboardPen } from "lucide-react";
import { Document, DocumentScope, DocumentType } from "protogen/documents_pb";
import useIsMobile from "components/hooks/useIsMobile";
import { dateObjectToshortDate } from "common/utils";
import { getScopeGroupLabel, isPrivate } from "./utils";
import EmptyTableOverlay from "components/common/EmptyTableOverlay";
import DropdownMenu from "components/common/DropdownMenu";
import LinkRouter from "components/navigation/LinkRouter";
import { DocumentFavoriteIcon } from "./DocumentFavoriteIcon";
import FlexBox from "components/common/FlexBox";
import { CurrentUser } from "protogen/auth_pb";
import { useContext } from "react";
import { CurrentUserContext } from "components/context/RequireAuth";

const FixedColumnWidth = "120px";

const VisibilityChip = ({ document }: { document: Document }) => {
  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {getScopeGroupLabel(document.scope) || ""}
        </Box>
      }
      variant={isPrivate(document) ? "outlined" : "green"}
      sx={{
        width: "85px",
      }}
    />
  );
};

const DocumentActionMenu = ({
  document,
  onDuplicate,
  onDelete,
}: {
  document: Document;
  onDuplicate: (document: Document) => void;
  onDelete: (document: Document) => void;
}) => {
  return (
    <DropdownMenu
      key={document.ref}
      disabled={false}
      fullWidth
      buttonContent={<EllipsisVertical size={20} color="#8E9598" />}
      dropdownIcon={false}
      variant="text"
      options={[
        {
          title: "Duplicate",
          onClick: () => onDuplicate(document),
        },
        {
          title: "Delete",
          onClick: () => onDelete(document),
        },
      ]}
      sx={{
        minWidth: "auto",
      }}
    />
  );
};

const TableHeader = () => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        height: "48px",
      }}
    >
      <Typography
        variant="bodyHeavy"
        color="text.primary"
        sx={{ width: "1%", flexGrow: 1 }}
      >
        Name
      </Typography>
      <Typography
        variant="bodyHeavy"
        color="text.primary"
        sx={{ width: FixedColumnWidth, textAlign: "end" }}
      >
        Owner
      </Typography>
      <Typography
        variant="bodyHeavy"
        color="text.primary"
        sx={{ width: FixedColumnWidth, textAlign: "center" }}
      >
        Visibility
      </Typography>
      <Typography
        variant="bodyHeavy"
        color="text.primary"
        sx={{ width: FixedColumnWidth, textAlign: "center" }}
      >
        Updated
      </Typography>
    </Box>
  );
};

const getDocOwner = (document: Document, currentUser: CurrentUser) => {
  const isAdmin = document.scope === DocumentScope.ADMIN;
  const owner = document.family?.name || (isAdmin ? "Faye" : "Me");
  if (document.family?.name) {
    return owner;
  } else if (document.scope === DocumentScope.ADMIN) {
    return "Faye";
  } else if (document.owner?.ref === currentUser.ref) {
    return "Me";
  } else {
    return document.owner?.displayName;
  }
};

const DocumentRow = ({
  document,
  onDuplicate,
  onDelete,
  showFavoriteCount = false,
  layout = "detailed",
}: {
  document: Document;
  onDuplicate: (document: Document) => void;
  onDelete: (document: Document) => void;
  showFavoriteCount?: boolean;
  layout?: "simple" | "detailed";
}) => {
  let meta: Record<string, any> = {};
  try {
    meta = document.meta ? JSON.parse(document.meta) : {};
  } catch (e) {}
  const isFormDefinition = meta.isFormDefinition;
  const formSubPath = isFormDefinition ? "create" : "complete";
  const currentUser = useContext(CurrentUserContext);
  const owner = getDocOwner(document, currentUser);
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        padding: "12px 0",
      }}
    >
      <FlexBox gap="12px">
        {layout === "detailed" && (
          <DocumentFavoriteIcon
            document={document}
            showCount={showFavoriteCount}
            size="small"
            showTooltip={false}
            isStacked={true}
            sx={{ marginTop: "2px" }}
          />
        )}

        <FlexBox direction="column" gap="0" sx={{ alignSelf: "center" }}>
          {/* Document title */}
          <LinkRouter
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: 0,
              width: "100%",
            }}
            to={
              document.documentType === DocumentType.FORM
                ? `/forms/${formSubPath}/${encodeURIComponent(document.uuid)}`
                : `/documents/${encodeURIComponent(document.uuid)}`
            }
            key={document.ref}
          >
            <Typography variant="bodyHeavy" color="text.primary">
              {document.title || "Untitled"}
            </Typography>
            {isFormDefinition && <ClipboardPen size={16} color="#8E9598" />}
          </LinkRouter>

          {/* Topics */}
          {document.topics?.length > 0 && (
            <FlexBox gap="4px">
              {document.topics.map((topic) => (
                <LinkRouter
                  to={`/documents/community/${encodeURIComponent(
                    topic.trim().toLowerCase(),
                  )}`}
                  sx={{ padding: 0 }}
                  key={topic}
                >
                  <Typography variant="bodySmall" color="text.secondary">
                    {topic}
                  </Typography>
                </LinkRouter>
              ))}
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>

      <Typography
        variant="bodySmall"
        color="text.primary"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: FixedColumnWidth,
          textAlign: "end",
        }}
      >
        {owner}
      </Typography>
      <Box
        sx={{
          display: "flex",
          width: FixedColumnWidth,
          flexDirection: "column",
          alignItems: "flex-end",
        }}
      >
        <VisibilityChip document={document} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          width: FixedColumnWidth,
          justifyContent: "flex-end",
          gap: "24px",
        }}
      >
        <Typography variant="bodySmall">
          {dateObjectToshortDate(
            new Date(Number(document.lastUpdatedSec) * 1000),
          )}
        </Typography>
        {layout === "detailed" && (
          <DocumentActionMenu
            document={document}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        )}
      </Box>
    </Box>
  );
};

const DocumentRowMobile = ({
  document,
  showFavoriteCount,
  layout = "detailed",
}: {
  document: Document;
  showFavoriteCount: boolean;
  layout?: "simple" | "detailed";
}) => {
  const currentUser = useContext(CurrentUserContext);
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        padding: "16px 0px",
        alignItems: "start",
      }}
    >
      {layout === "detailed" && (
        <DocumentFavoriteIcon
          document={document}
          showCount={showFavoriteCount}
        />
      )}
      <FlexBox gap="0" direction="column">
        <Typography variant="bodyHeavy" color="text.primary">
          {document.title || "Untitled"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Typography
              variant="body"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getDocOwner(document, currentUser)}
            </Typography>
            <Typography variant="body" color="text.secondary">
              •
            </Typography>
            <Typography
              variant="body"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              {dateObjectToshortDate(
                new Date(Number(document.lastUpdatedSec) * 1000),
              )}
            </Typography>
          </Box>
          <VisibilityChip document={document} />
        </Box>
      </FlexBox>
    </Box>
  );
};

type Props = {
  documents: Document[];
  loading?: boolean;
  onDuplicate?: (document: Document) => void;
  onDelete?: (document: Document) => void;
  showFavoriteCount?: boolean;
  layout?: "simple" | "detailed";
};

export default ({
  documents,
  loading,
  onDuplicate,
  onDelete,
  showFavoriteCount = false,
  layout = "detailed",
}: Props) => {
  const isMobile = useIsMobile();

  return (
    <Box>
      {(loading || documents.length === 0) && EmptyTableOverlay("No Documents")}
      {!isMobile && (
        <Box>
          <TableHeader />
          <Divider />
          {documents.map((document) => {
            return (
              <Box
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
                key={document.ref}
              >
                <DocumentRow
                  document={document}
                  onDuplicate={onDuplicate || (() => {})}
                  onDelete={onDelete || (() => {})}
                  showFavoriteCount={showFavoriteCount}
                  layout={layout}
                />
                <Divider />
              </Box>
            );
          })}
        </Box>
      )}
      {isMobile && (
        <Box>
          {documents.map((document) => {
            return (
              <Box
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
                key={document.ref}
              >
                <LinkRouter
                  to={
                    document.documentType === DocumentType.FORM
                      ? `/forms/complete/${encodeURIComponent(document.uuid)}`
                      : `/documents/${encodeURIComponent(document.uuid)}`
                  }
                >
                  <DocumentRowMobile
                    document={document}
                    showFavoriteCount={showFavoriteCount}
                    layout={layout}
                  />
                </LinkRouter>
                <Divider />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
